import React from 'react';
import { Dialog, DialogContent } from './ui/dialog';
import { X, Leaf, FlaskConical, Package, ShieldAlert } from 'lucide-react';
import { useLang } from '../LanguageContext';

const Row = ({ label, value }) => {
  if (!value) return null;
  return (
    <div className="flex items-center justify-between py-3 border-b border-[#0d1b36]/10 last:border-b-0">
      <span className="text-[11px] tracking-[0.2em] uppercase text-[#0d1b36]/55">{label}</span>
      <span className="text-sm text-[#0d1b36] font-medium">{value}</span>
    </div>
  );
};

const SectionTitle = ({ icon: Icon, children }) => (
  <div className="flex items-center gap-3 mb-4">
    <div className="w-9 h-9 rounded-full border border-[#d4a94c]/50 flex items-center justify-center bg-[#f6ecd9]">
      <Icon className="w-4 h-4 text-[#d4a94c]" strokeWidth={1.5} />
    </div>
    <h4 className="font-serif text-xl text-[#0d1b36]">{children}</h4>
  </div>
);

const FicheModal = ({ open, onClose, item, type }) => {
  const { t } = useLang();
  if (!item) return null;
  const isPate = type === 'pate';

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-4xl p-0 overflow-hidden bg-[#f6ecd9] border-[#d4a94c]/30 max-h-[92vh] overflow-y-auto">
        <div className="grid md:grid-cols-5 relative">
          <button
            onClick={onClose}
            className="absolute top-4 right-4 z-20 w-10 h-10 rounded-full bg-[#0d1b36] text-[#f6ecd9] flex items-center justify-center hover:bg-[#d4a94c] hover:text-[#0d1b36] transition-colors"
            aria-label={t.modal.close}
          >
            <X className="w-4 h-4" strokeWidth={2} />
          </button>

          <div className="md:col-span-2 bg-white relative flex items-center justify-center p-8 min-h-[280px]">
            {isPate ? (
              <>
                <div className="absolute top-5 left-5 px-3 py-1 rounded-full bg-[#0d1b36] text-[#f6ecd9] text-[10px] tracking-[0.25em] uppercase">
                  {item.tag}
                </div>
                <div className="absolute top-5 right-5 px-3 py-1 rounded-full bg-[#d4a94c] text-[#0d1b36] text-[10px] tracking-[0.2em] font-semibold">
                  {item.weight}
                </div>
              </>
            ) : (
              <div className="absolute top-5 left-5 px-3 py-1 rounded-full bg-[#0d1b36] text-[#f6ecd9] text-[10px] tracking-[0.3em] uppercase">
                N°{item.num}
              </div>
            )}
            <img
              src={item.image}
              alt={item.name}
              className={`w-full ${isPate ? 'object-contain max-h-72' : 'object-cover aspect-square rounded-xl'}`}
              style={!isPate && item.imagePosition ? { objectPosition: item.imagePosition } : undefined}
            />
          </div>

          <div className="md:col-span-3 p-8 md:p-10">
            <p className="text-[11px] tracking-[0.3em] uppercase text-[#d4a94c] mb-3">
              {isPate ? t.modal.pate : t.modal.pastry}
            </p>
            <h3 className="font-serif text-4xl md:text-5xl text-[#0d1b36] leading-[1.05] mb-3">
              {item.name}
            </h3>
            <p className="text-[11px] tracking-[0.2em] uppercase text-[#0d1b36]/60 mb-6">
              {item.subtitle}
            </p>
            <p className="text-[#0d1b36]/75 text-sm leading-relaxed mb-8">
              {item.description}
            </p>

            <div className="mb-8">
              <SectionTitle icon={Leaf}>{t.modal.ingredients}</SectionTitle>
              <p className="text-sm text-[#0d1b36]/75 leading-relaxed pl-12">
                {item.ingredients}
              </p>
            </div>

            {item.nutrition && (
              <div className="mb-8">
                <SectionTitle icon={FlaskConical}>
                  {t.modal.nutrition} <span className="text-xs text-[#0d1b36]/50 font-sans not-italic ml-1">{t.modal.per100}</span>
                </SectionTitle>
                <div className="pl-12">
                  <Row label={t.modal.energy} value={item.nutrition.energy} />
                  <Row label={t.modal.fat} value={item.nutrition.fat} />
                  <Row label={t.modal.sat} value={item.nutrition.sat} />
                  <Row label={t.modal.carbs} value={item.nutrition.carbs} />
                  <Row label={t.modal.sugars} value={item.nutrition.sugars} />
                  <Row label={t.modal.protein} value={item.nutrition.protein} />
                  <Row label={t.modal.salt} value={item.nutrition.salt} />
                </div>
              </div>
            )}

            {!isPate && item.allergens && (
              <div className="mb-8">
                <SectionTitle icon={ShieldAlert}>{t.modal.allergens}</SectionTitle>
                <p className="text-sm text-[#0d1b36]/75 leading-relaxed pl-12">
                  {item.allergens}
                </p>
              </div>
            )}

            <div>
              <SectionTitle icon={Package}>{t.modal.conditioning}</SectionTitle>
              <div className="pl-12">
                {isPate ? (
                  <>
                    <Row label={t.modal.shelf} value={item.conditioning.shelfLife} />
                    <Row label={t.modal.storage} value={item.conditioning.storage} />
                    <Row label={t.modal.packaging} value={item.conditioning.packaging} />
                    <Row label={t.modal.cartons} value={item.conditioning.cartons} />
                    <Row label={t.modal.pallet} value={item.conditioning.pallet} />
                  </>
                ) : (
                  <>
                    <Row label={t.modal.pb} value={item.conditioning.pb} />
                    <Row label={t.modal.gb} value={item.conditioning.gb} />
                    <Row label={t.modal.tgb} value={item.conditioning.tgb} />
                  </>
                )}
              </div>
            </div>

            <div className="mt-8 pt-6 border-t border-[#0d1b36]/10 flex flex-wrap gap-2">
              <span className="px-3 py-1.5 rounded-full border border-[#0d1b36]/20 text-[#0d1b36]/70 text-[10px] tracking-[0.25em] uppercase">
                {t.modal.madeFr}
              </span>
              {isPate && (
                <span className="px-3 py-1.5 rounded-full border border-[#0d1b36]/20 text-[#0d1b36]/70 text-[10px] tracking-[0.25em] uppercase">
                  {t.modal.atm}
                </span>
              )}
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default FicheModal;
